import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import {
  useLocation,
} from 'react-router-dom';

import {
  useAuth,
} from '../auth/AuthContext';

import {
  PRODUCT_CATALOG,
} from '../product-catalog/productCatalog';

import {
  createInquiry,
  createPublicInquiry,
} from './services/contactService';

import './ContactPage.css';

import {
  CONTACT_FIELD_LIMITS,
  formatContactIssue,
  getContactLengthError,
  isValidContactEmail,
} from './contactValidation';

const INQUIRY_TYPE_VALUES =
  Object.freeze([
    'technical',
    'license',
    'resource',
    'account',
    'other',
  ]);


const FORM_LIMITS =
  Object.freeze({
    SUBJECT_MAX_LENGTH:
      100,

    MESSAGE_MAX_LENGTH:
      2000,
  });


const INITIAL_INQUIRY_TYPE =
  'technical';


const GUEST_PRODUCT_CODE =
  'HI_PORTAL';


const GUEST_INQUIRY_TYPE =
  'account';

const GUEST_SERVICE_INQUIRY_TYPE =
  'service';


function getCompanyName(
  user,
) {
  return (
    user?.company?.name ||
    user?.companyName ||
    ''
  );
}


function getUserEmail(
  user,
) {
  return (
    user?.email ||
    user?.userEmail ||
    ''
  );
}


function getUserPhone(
  user,
) {
  return (
    user?.phone ||
    user?.phoneNumber ||
    user?.mobile ||
    ''
  );
}


function createInitialForm(
  user,
  isGuest,
  guestInquiryType,
  isEquipmentInquiry,
  equipmentName,
) {
  return {
    companyName:
      getCompanyName(
        user,
      ),

    email:
      getUserEmail(
        user,
      ),

    phone:
      getUserPhone(
        user,
      ),

    productCode:
      isEquipmentInquiry
        ? equipmentName
        : isGuest
          ? GUEST_PRODUCT_CODE
          : '',

    inquiryType:
      isEquipmentInquiry
        ? 'equipment'
        : isGuest
          ? guestInquiryType
          : INITIAL_INQUIRY_TYPE,

    subject:
      '',

    message:
      '',

    attachment:
      null,
  };
}


export default function ContactPage() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      'app',
    );


  const {
    user,
    isAuthenticated,
  } =
    useAuth();


  const isGuest =
    !isAuthenticated;


  const location =
    useLocation();


  const isEquipmentInquiry =
    location.state?.inquiryType === 'equipment';


  const equipmentName =
    isEquipmentInquiry && typeof location.state?.equipmentName === 'string'
      ? location.state.equipmentName
      : '';


  const guestInquiryType =
    location.state?.inquiryType ===
    GUEST_INQUIRY_TYPE
      ? GUEST_INQUIRY_TYPE
      : GUEST_SERVICE_INQUIRY_TYPE;


  const initialForm =
    useMemo(
      () =>
        createInitialForm(
          user,
          isGuest,
          guestInquiryType,
          isEquipmentInquiry,
          equipmentName,
        ),
      [
        user,
        isGuest,
        guestInquiryType,
        isEquipmentInquiry,
        equipmentName,
      ],
    );


  const [
    form,
    setForm,
  ] =
    useState(
      initialForm,
    );


  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState(
      '',
    );


  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(
      false,
    );


  useEffect(
    () => {
      setForm(
        createInitialForm(
          user,
          isGuest,
          guestInquiryType,
          isEquipmentInquiry,
          equipmentName,
        ),
      );

      setErrorMessage(
        '',
      );
    },
    [
      user,
      isGuest,
      guestInquiryType,
      isEquipmentInquiry,
      equipmentName,
    ],
  );


  const handleInputChange = (
    event,
  ) => {
    const {
      name,
      value,
      files,
      type,
    } =
      event.target;


    setForm(
      (
        previousForm,
      ) => ({
        ...previousForm,

        [name]:
          type ===
            'file'
            ? files?.[0] ||
            null
            : value,
      }),
    );


    if (
      errorMessage
    ) {
      setErrorMessage(
        '',
      );
    }
  };


  const validateForm =
    () => {
      const lengthError = getContactLengthError(form, t);
      if (lengthError) return lengthError;

      if (
        !form.companyName
          .trim()
      ) {
        return t(
          'contact.validation.companyName',
        );
      }


      if (
        !form.email
          .trim()
      ) {
        return t(
          'contact.validation.email',
        );
      }


      if (
        !isValidContactEmail(
          form.email,
        )
      ) {
        return t(
          'contact.validation.invalidEmail',
        );
      }


      if (
        !isEquipmentInquiry &&
        !form.productCode
      ) {
        return t(
          'contact.validation.product',
        );
      }


      if (
        !form.inquiryType
      ) {
        return t(
          'contact.validation.inquiryType',
        );
      }


      if (
        !form.subject
          .trim()
      ) {
        return t(
          'contact.validation.subject',
        );
      }


      if (
        !form.message
          .trim()
      ) {
        return t(
          'contact.validation.message',
        );
      }


      return '';
    };


  const handleReset =
    () => {
      setForm(
        createInitialForm(
          user,
          isGuest,
          guestInquiryType,
          isEquipmentInquiry,
          equipmentName,
        ),
      );

      setErrorMessage(
        '',
      );
    };


  const handleSubmit =
    async (
      event,
    ) => {
      event.preventDefault();


      const validationMessage =
        validateForm();


      if (
        validationMessage
      ) {
        setErrorMessage(
          validationMessage,
        );

        return;
      }


      setIsSubmitting(
        true,
      );

      setErrorMessage(
        '',
      );


      const locale =
        i18n.resolvedLanguage ||
        i18n.language ||
        'ko';


      try {
        if (
          isGuest
        ) {
          await createPublicInquiry({
            productCode:
              form.productCode,

            companyName:
              form.companyName,

            email:
              form.email,

            phone:
              form.phone,

            inquiryType:
              form.inquiryType,

            subject:
              form.subject,

            message:
              form.message,

            locale,
          });
        } else {
          await createInquiry({
            companyName:
              form.companyName,

            email:
              form.email,

            phone:
              form.phone,

            productCode:
              form.productCode,

            inquiryType:
              form.inquiryType,

            subject:
              form.subject,

            message:
              form.message,

            locale,
          });
        }


        window.alert(
          t(
            'contact.submitSuccess',
          ),
        );


        handleReset();
      } catch (
      error
      ) {
        console.error(
          '[ContactPage] Inquiry submission failed:',
          error,
        );

        if (error?.code === 'INVALID_INPUT') {
          setErrorMessage(formatContactIssue(error.data?.data?.issues?.[0], t));
          return;
        }


        if (
          error?.code ===
          'SALES_EMAIL_NOT_FOUND'
        ) {
          setErrorMessage(
            t(
              'contact.submitSalesEmailMissing',
            ),
          );

          return;
        }


        if (
          error?.code ===
          'SALES_REPRESENTATIVE_REQUIRED' ||
          error?.code ===
          'INVALID_SALES_REPRESENTATIVE'
        ) {
          setErrorMessage(
            t(
              'signup.errors.salesRequired',
            ),
          );

          return;
        }


        setErrorMessage(
          t(
            'contact.submitFailed',
          ),
        );
      } finally {
        setIsSubmitting(
          false,
        );
      }
    };


  return (
    <>
      <div
        className="contact-public-page"
      >
        <main
          className="contact-main"
        >
          <section
            className="contact-hero"
          >
            <div
              className="contact-hero-inner"
            >
              <span
                className="contact-hero-eyebrow"
              >
                CONTACT US
              </span>

              <h1>
                {t(
                  'navigation.contact',
                )}
              </h1>

              <p>
                {t(
                  'home.contact.description',
                )}
              </p>
            </div>
          </section>

          <section
            className="contact-content"
          >
            <div
              className="contact-content-inner"
            >
              <div
                className="contact-page"
              >
                <section
                  className="contact-form-card"
                >
                  <header
                    className="contact-section-heading"
                  >
                    <h2>
                      {t(
                        'contact.formTitle',
                      )}
                    </h2>

                    <p>
                      {t(
                        'contact.formDescription',
                      )}
                    </p>
                  </header>


                  <form
                    className="contact-form"
                    onSubmit={
                      handleSubmit
                    }
                  >
                    <div
                      className="contact-form-grid"
                    >

                      <div
                        className="contact-form-field"
                      >
                        <label
                          htmlFor="companyName"
                        >
                          {t(
                            'contact.fields.companyName.label',
                          )}

                          <span>
                            *
                          </span>
                        </label>

                        <input
                          id="companyName"
                          name="companyName"
                          type="text"
                          maxLength={CONTACT_FIELD_LIMITS.companyName}
                          value={
                            form.companyName
                          }
                          onChange={
                            handleInputChange
                          }
                          placeholder={
                            t(
                              'contact.fields.companyName.placeholder',
                            )
                          }
                          autoComplete="organization"
                          disabled={
                            isSubmitting
                          }
                        />
                      </div>


                      <div
                        className="contact-form-field"
                      >
                        <label
                          htmlFor="email"
                        >
                          {t(
                            'contact.fields.email.label',
                          )}

                          <span>
                            *
                          </span>
                        </label>

                        <input
                          id="email"
                          name="email"
                          type="email"
                          maxLength={CONTACT_FIELD_LIMITS.email}
                          value={
                            form.email
                          }
                          onChange={
                            handleInputChange
                          }
                          placeholder={
                            t(
                              'contact.fields.email.placeholder',
                            )
                          }
                          autoComplete="email"
                          disabled={
                            isSubmitting
                          }
                        />
                      </div>


                      <div
                        className="contact-form-field"
                      >
                        <label
                          htmlFor="phone"
                        >
                          {t(
                            'contact.fields.phone.label',
                          )}
                        </label>

                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          maxLength={CONTACT_FIELD_LIMITS.phone}
                          value={
                            form.phone
                          }
                          onChange={
                            handleInputChange
                          }
                          placeholder={
                            t(
                              'contact.fields.phone.placeholder',
                            )
                          }
                          autoComplete="tel"
                          disabled={
                            isSubmitting
                          }
                        />
                      </div>


                      <div
                        className="contact-form-field"
                      >
                        <label
                          htmlFor="productCode"
                        >
                          {t(
                            'contact.fields.product.label',
                          )}

                          {!isEquipmentInquiry && <span>*</span>}
                        </label>


                        {isEquipmentInquiry ? (
                          <select id="productCode" name="productCode" value={form.productCode} disabled>
                            <option value={form.productCode}>{form.productCode}</option>
                          </select>
                        ) : isGuest ? (
                          <select
                            id="productCode"
                            name="productCode"
                            value={
                              GUEST_PRODUCT_CODE
                            }
                            disabled
                          >
                            <option
                              value={
                                GUEST_PRODUCT_CODE
                              }
                            >
                              Hi-Portal
                            </option>
                          </select>
                        ) : (
                          <select
                            id="productCode"
                            name="productCode"
                            value={
                              form.productCode
                            }
                            onChange={
                              handleInputChange
                            }
                            disabled={
                              isSubmitting
                            }
                          >
                            <option
                              value=""
                            >
                              {t(
                                'contact.fields.product.placeholder',
                              )}
                            </option>

                            {PRODUCT_CATALOG.map(
                              (
                                product,
                              ) => (
                                <option
                                  key={
                                    product.id ||
                                    product.code
                                  }
                                  value={
                                    product.code
                                  }
                                >
                                  {
                                    product.name
                                  }
                                </option>
                              ),
                            )}
                          </select>
                        )}
                      </div>


                      <div
                        className="contact-form-field"
                      >
                        <label
                          htmlFor="inquiryType"
                        >
                          {t(
                            'contact.fields.inquiryType.label',
                          )}

                          <span>
                            *
                          </span>
                        </label>


                        {isGuest || isEquipmentInquiry ? (
                          <select
                            id="inquiryType"
                            name="inquiryType"
                            value={
                              form.inquiryType
                            }
                            disabled
                          >
                            <option
                              value={
                                form.inquiryType
                              }
                            >
                              {t(
                                `contact.inquiryTypes.${form.inquiryType}`,
                              )}
                            </option>
                          </select>
                        ) : (
                          <select
                            id="inquiryType"
                            name="inquiryType"
                            value={
                              form.inquiryType
                            }
                            onChange={
                              handleInputChange
                            }
                            disabled={
                              isSubmitting
                            }
                          >
                            {INQUIRY_TYPE_VALUES.map(
                              (
                                inquiryType,
                              ) => (
                                <option
                                  key={
                                    inquiryType
                                  }
                                  value={
                                    inquiryType
                                  }
                                >
                                  {t(
                                    `contact.inquiryTypes.${inquiryType}`,
                                  )}
                                </option>
                              ),
                            )}
                          </select>
                        )}
                      </div>


                      <div
                        className="
                contact-form-field
                contact-form-field-full
              "
                      >
                        <label
                          htmlFor="subject"
                        >
                          {t(
                            'contact.fields.subject.label',
                          )}

                          <span>
                            *
                          </span>
                        </label>

                        <input
                          id="subject"
                          name="subject"
                          type="text"
                          value={
                            form.subject
                          }
                          onChange={
                            handleInputChange
                          }
                          placeholder={
                            t(
                              'contact.fields.subject.placeholder',
                            )
                          }
                          maxLength={
                            FORM_LIMITS.SUBJECT_MAX_LENGTH
                          }
                          disabled={
                            isSubmitting
                          }
                        />

                        <small>
                          {t(
                            'contact.characterCount',
                            {
                              current:
                                form.subject.length,

                              maximum:
                                FORM_LIMITS.SUBJECT_MAX_LENGTH,
                            },
                          )}
                        </small>
                      </div>


                      <div
                        className="
                contact-form-field
                contact-form-field-full
              "
                      >
                        <label
                          htmlFor="message"
                        >
                          {t(
                            'contact.fields.message.label',
                          )}

                          <span>
                            *
                          </span>
                        </label>

                        <textarea
                          id="message"
                          name="message"
                          value={
                            form.message
                          }
                          onChange={
                            handleInputChange
                          }
                          placeholder={
                            t(
                              'contact.fields.message.placeholder',
                            )
                          }
                          maxLength={
                            FORM_LIMITS.MESSAGE_MAX_LENGTH
                          }
                          rows={9}
                          disabled={
                            isSubmitting
                          }
                        />

                        <small>
                          {t(
                            'contact.characterCount',
                            {
                              current:
                                form.message.length,

                              maximum:
                                FORM_LIMITS.MESSAGE_MAX_LENGTH,
                            },
                          )}
                        </small>
                      </div>


                      <div
                        className="
                contact-form-field
                contact-form-field-full
              "
                      >
                        <label
                          htmlFor="attachment"
                        >
                          {t(
                            'contact.fields.attachment.label',
                          )}
                        </label>

                        <label
                          className="contact-file-input"
                          htmlFor="attachment"
                        >
                          <input
                            id="attachment"
                            name="attachment"
                            type="file"
                            onChange={
                              handleInputChange
                            }
                            disabled={
                              isSubmitting
                            }
                          />

                          <span>
                            {form.attachment
                              ? form.attachment.name
                              : t(
                                'contact.fields.attachment.placeholder',
                              )}
                          </span>

                          <strong>
                            {t(
                              'contact.fields.attachment.button',
                            )}
                          </strong>
                        </label>

                        <p
                          className="contact-file-description"
                        >
                          {t(
                            'contact.fields.attachment.description',
                          )}
                        </p>
                      </div>
                    </div>


                    {errorMessage && (
                      <p
                        className="contact-form-error"
                        role="alert"
                      >
                        {
                          errorMessage
                        }
                      </p>
                    )}


                    <div
                      className="contact-form-actions"
                    >
                      <button
                        type="button"
                        className="contact-reset-button"
                        onClick={
                          handleReset
                        }
                        disabled={
                          isSubmitting
                        }
                      >
                        {t(
                          'contact.actions.reset',
                        )}
                      </button>

                      <button
                        type="submit"
                        className="contact-submit-button"
                        disabled={
                          isSubmitting
                        }
                      >
                        {isSubmitting
                          ? t(
                            'contact.actions.submitting',
                          )
                          : t(
                            'contact.actions.submit',
                          )}
                      </button>
                    </div>
                  </form>
                </section>


                <aside
                  className="contact-guide-column"
                >
                  <section
                    className="contact-guide-card"
                  >
                    <h3>
                      {t(
                        'contact.guide.title',
                      )}
                    </h3>

                    <ol
                      className="contact-guide-list"
                    >
                      <li>
                        <span>
                          01
                        </span>

                        <p>
                          {t(
                            'contact.guide.items.product',
                          )}
                        </p>
                      </li>

                      <li>
                        <span>
                          02
                        </span>

                        <p>
                          {t(
                            'contact.guide.items.reproduction',
                          )}
                        </p>
                      </li>

                      <li>
                        <span>
                          03
                        </span>

                        <p>
                          {t(
                            'contact.guide.items.attachment',
                          )}
                        </p>
                      </li>
                    </ol>
                  </section>


                  <section
                    className="contact-guide-card"
                  >
                    <h3>
                      {t(
                        'contact.process.title',
                      )}
                    </h3>

                    <div
                      className="contact-process-list"
                    >
                      <div>
                        <span>
                          1
                        </span>

                        <strong>
                          {t(
                            'contact.process.steps.register',
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>
                          2
                        </span>

                        <strong>
                          {t(
                            'contact.process.steps.review',
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>
                          3
                        </span>

                        <strong>
                          {t(
                            'contact.process.steps.response',
                          )}
                        </strong>
                      </div>
                    </div>

                    <p
                      className="contact-guide-description"
                    >
                      {t(
                        'contact.process.description',
                      )}
                    </p>
                  </section>
                </aside>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
